<template>
  <div>
    <Alert v-if="amountDue > 0" type="info" class="mb-4">
      Montant restant à percevoir : {{ $n(amountDue, "currency") }}
    </Alert>
    <SearchToolbar
      class="mb-6"
      :placeholder="$t('Rechercher une facture de sinistre')"
      :initial-keywords="keywords"
      @update-keyword-search="updateKeywords"
    >
      <DateRangePicker placeholder="Période" v-model="date" />
    </SearchToolbar>
    <Table
      v-if="claimInvoices.length > 0"
      :columns="columns"
      :data="claimInvoices"
      :isDownloadable="true"
      :isAccessible="true"
      @download="download"
      @view="rowClick"
      @sort="sortBy"
    >
      <template #cost="{ row }">
        <div class="flex gap-x-6">
          <div class="flex-auto">
            <div class="flex items-start gap-x-3">
              <div class="font-medium text-gray-900">{{ $n(row.repairCost || 0, "currency") }}</div>
            </div>
          </div>
        </div>
      </template>
      <template #paid="{ row }">
        <div class="flex gap-x-6">
          <div class="flex-auto">
            <div class="flex items-start gap-x-3">
              <div
                :class="[
                  row.paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800',
                  'rounded-md px-2 text-xs font-medium',
                ]"
              >
                {{ row.paid ? $t("paid") : $t("Pending payment") }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </Table>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
  </div>
</template>

<script>
import { Alert, DateRangePicker, Pagination, SearchToolbar, Table, isPresent } from "@estaly/ui";
import ClaimsService from "@/api/services/claims";
import { toCamelCase } from "@/utils/utils";

export default {
  props: {
    claimDetailsRoutePrefix: {
      type: String,
      required: true,
    },
    storeId: {
      type: Number,
      required: false,
    },
  },

  components: {
    Alert,
    DateRangePicker,
    SearchToolbar,
    Pagination,
    Table,
  },

  data() {
    return {
      claimInvoices: [],
      amountDue: null,
      keywords: null,
      startDate: null,
      endDate: null,
      currentPage: 1,
      totalCount: null,
      totalPages: null,
      sortColumn: null,
      sortOrder: null,
      order: null,
      columns: [
        { label: "Sinistres", field: "friendlyId" },
        { label: "Date de résolution", field: "resolutionDate", sortable: true },
        { label: "Type", field: "handlingType" },
        { label: "Magasin", field: "store" },
        { label: "Montant facture", slot: "cost", field: "repairCost" },
        { label: "Statut", slot: "paid", field: "paid" },
        { label: "Date de paiement", field: "paymentDate" },
      ],
    };
  },

  watch: {
    date() {
      this.handleChangePage(1);
    },
  },

  computed: {
    params() {
      return {
        page: this.currentPage,
        keywords: this.keywords,
        order: this.order,
        startDate: this.startDate,
        endDate: this.endDate,
        storeId: this.storeId,
        approvalStatus: "approved",
        claimStatus: "resolved",
      };
    },

    date: {
      get() {
        return [this.startDate, this.endDate];
      },
      set(value) {
        if (value && value.length === 2) {
          this.startDate = value[0];
          this.endDate = value[1];
        } else {
          this.startDate = null;
          this.endDate = null;
        }
      },
    },
  },

  mounted() {
    this.retrieveClaimInvoices();
  },

  methods: {
    async retrieveClaimInvoices() {
      if (this.sortColumn) {
        const direction = this.sortOrder === "ASC" ? "" : "-";
        const columnEquivalent = this.sortColumn === "resolutionDate" ? "claim.resolution_date" : this.sortColumn;
        this.order = `${direction}${columnEquivalent}`;
      }

      const response = await ClaimsService.getClaimInvoices(this.params);
      if (!response?.success) return;

      const formattedResponse = toCamelCase(response.data);
      this.claimInvoices = formattedResponse.claimInvoices;
      this.amountDue = formattedResponse.amountDue;
      this.totalCount = formattedResponse.page.totalCount;
      this.totalPages = formattedResponse.page.totalPages;
    },

    handleChangePage(newPage) {
      this.currentPage = newPage;
      this.retrieveClaimInvoices();
    },

    updateKeywords(keywords) {
      this.keywords = keywords;
      this.handleChangePage(1);
    },

    download(row) {
      window.open(row.url, "_blank");
    },

    rowClick(row) {
      window.open(`${this.claimDetailsRoutePrefix}/${row.friendlyId}`, "_blank");
    },

    sortBy(column) {
      if (!isPresent(this.claimInvoices)) return;
      this.sortColumn = column.field;
      this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
      this.handleChangePage(1);
    },
  },
};
</script>
